import * as React from "react";
import { Form, Container, Button } from "semantic-ui-react";
import { toast } from "@teamprevent/common/toasts";

import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";

import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { MedicalType } from "@teamprevent/common/healthSurveillance/models";

import { PartialOhCaseDetailModel } from "../../crud/ohCase";

import { TaskType, CreateTaskModel, mapAddTaskFormModelToCreateTaskModel } from "../../task/model";

import { ImmunisationTypeTask } from "../../crud/immunisationTypeTask";
import { ImmunisationType } from "../../crud/immunisationType";

import { AddNextTaskFormModel } from "../models";

import { AddTasksInputs } from "./addTasksInputs";
import { ConfirmCaseCloseModal } from "./ConfirmCaseCloseModal";

interface OwnProps {
    onSubmit: (createTasks: CreateTaskModel[]) => void;
    onCancel?: () => void;
    taskTypes: TaskType[];
    immunisationTypes?: ImmunisationType[];
    immunisationTypeTasks?: ImmunisationTypeTask[];
    creatingTask: boolean;
    ohCaseId: string;
    ohCase: PartialOhCaseDetailModel;
    singleTask?: boolean;
    defaultDueDate?: boolean;
    submitButtonText?: string;
    medicalTypes?: MedicalType[];
    caseType?: string;
    assignmentOptionRequired?: boolean;
}

export const AddTasksForm: React.FunctionComponent<OwnProps> = (
    {
        onSubmit,
        onCancel,
        ohCase,
        ohCaseId,
        ...props
    }) => {
    const enforceMandatoryNotesForInsufficientInfoTasks = getFeatureToggles()[FeatureToggleNames.EnforceMandatoryNotesForInsufficientInfoTasks];

    const [submitted, setSubmitted] = React.useState<boolean>(false);
    const [confirmCloseOpen, setConfirmCloseOpen] = React.useState<boolean>(false);
    const [nextTasks, setNextTasks] = React.useState<AddNextTaskFormModel[]>([]);

    const handleOnSubmit = React.useCallback(() => {
        setSubmitted(true);

        if (nextTasks.some(t => !t.isValid)) {
            return;
        }

        if (enforceMandatoryNotesForInsufficientInfoTasks) {
            const tasksWithRequiredNotes = nextTasks.filter(t => t.task?.taskType?.notesRequired && !t.task.notes);
            if (tasksWithRequiredNotes.length > 0) {
                tasksWithRequiredNotes.forEach(t => {
                    toast.warning(`Notes required for "${t.task?.taskType?.description}" task`);
                });

                return;
            }
        }

        onSubmit(nextTasks.map(t => mapAddTaskFormModelToCreateTaskModel(t.task!, ohCaseId)));
    }, [nextTasks, enforceMandatoryNotesForInsufficientInfoTasks, onSubmit, ohCaseId]);

    const handleConfirmCaseModalOnCancel = React.useCallback(() => setConfirmCloseOpen(false), []);

    const onUpdateTasks = React.useCallback((updatedTasks: AddNextTaskFormModel[]) => setNextTasks(updatedTasks), []);

    const handleFormSubmitWithCaseCloseCheck = React.useCallback(() => {
        if ((ohCase.tasks?.every(t => t.completed))
            && nextTasks.every(t => t.task?.taskType?.closesCase)) {
            setConfirmCloseOpen(true);
            return;
        }

        handleOnSubmit();
    }, [handleOnSubmit, ohCase, nextTasks]);

    return (
        <>
            <Form onSubmit={handleFormSubmitWithCaseCloseCheck}>
                <AddTasksInputs
                    defaultDueDate={props.defaultDueDate}
                    onChange={onUpdateTasks}
                    taskTypes={props.taskTypes}
                    immunisationTypes={props.immunisationTypes}
                    immunisationTypeTasks={props.immunisationTypeTasks}
                    ohCaseId={ohCaseId}
                    showErrors={submitted}
                    singleTask={props.singleTask}
                    medicalTypes={props.medicalTypes}
                    caseType={props.caseType}
                    assignmentOptionRequired={props.assignmentOptionRequired}
                />
                {props.children}
                <Container textAlign="right" fluid>
                    {onCancel && <Button
                        content="Cancel"
                        onClick={onCancel}
                    />}
                    <Button
                        icon="add"
                        content={props.submitButtonText || `Add Task${nextTasks.length > 1 ? "s" : ""}`}
                        loading={props.creatingTask}
                        disabled={props.creatingTask}
                    />
                </Container>
            </Form>
            <ConfirmCaseCloseModal
                loading={props.creatingTask}
                isOpen={confirmCloseOpen}
                onSubmit={handleOnSubmit}
                onCancel={handleConfirmCaseModalOnCancel}
            />
        </>
    );
};
